import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { api, ApiError } from '../api/client'
import type { Store } from '../api/types'

// L'ambito lo decide il server, che espone solo i punti vendita autorizzati: chi ne ha uno
// entra su quello, chi ne ha più di uno li vede tutti e il selettore è un filtro di vista.
// La SELEZIONE però si ricorda, per utente e sul server (kpo.user_pref): chi lavora su due
// negozi li ritrova scelti alla prossima apertura, su qualunque dispositivo. Non è
// un'abilitazione — al caricamento si interseca comunque con i punti vendita autorizzati.

interface StoreContextValue {
  stores: Store[]
  selected: string[]
  loading: boolean
  error?: ApiError

  /**
   * La selezione nella forma da passare all'API: vuota quando i punti vendita spuntati sono
   * tutti (o nessuno), perché filtrare su tutti equivale a non filtrare.
   *
   * <p>Non è un'ottimizzazione: i punti vendita sono più di settecento, e un filtro esplicito su
   * tutti produce una URL di oltre diecimila caratteri che il server rifiuta con «414 URI Too
   * Long». Le schermate usano questa, non <c>selected</c>, che resta la verità dell'interfaccia
   * — le caselle vanno mostrate spuntate.</p>
   */
  storeFilter: string[]

  /**
   * Vero quando l'elenco dei punti vendita è noto e <c>storeFilter</c> si può usare.
   *
   * <p>Le schermate aspettano questo prima di interrogare l'API: senza l'elenco non si sa se
   * «tutti spuntati» siano tutti, e il filtro per esteso produce la URL che il server rifiuta.</p>
   */
  storesReady: boolean

  select: (codes: string[]) => void
  toggle: (code: string) => void

  reload: () => void
  storeName: (code: string) => string
}

const StoreContext = createContext<StoreContextValue | undefined>(undefined)

export function StoreProvider({ children }: { children: ReactNode }) {
  const [stores, setStores] = useState<Store[]>([])
  const [selected, setSelected] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<ApiError>()

  // La preferenza salvata si applica una volta, al caricamento; da lì in poi ogni modifica
  // della selezione viene rispedita al server con un piccolo ritardo (chi spunta cinque
  // caselle non deve produrre cinque salvataggi).
  const prefsApplied = useRef(false)
  const saveTimer = useRef<number>(undefined)

  const load = useCallback(() => {
    setLoading(true)
    setError(undefined)

    Promise.all([
      api.get<Store[]>('/stores'),
      api.get<{ selectedStores: string[] }>('/me/preferences').catch(() => ({ selectedStores: [] })),
    ])
      .then(([storeData, prefs]) => {
        const usable = storeData.filter((s) => !s.isCedi)
        setStores(usable)

        const allowed = new Set(usable.map((s) => s.code))

        if (!prefsApplied.current) {
          prefsApplied.current = true
          // La selezione ricordata vale solo fra i punti vendita ancora autorizzati; con un
          // solo punto vendita non c'è niente da ricordare.
          const remembered = usable.length > 1
            ? prefs.selectedStores.filter((c) => allowed.has(c))
            : []
          if (remembered.length > 0) setSelected(remembered)
          return
        }

        // Ricaricamento: una selezione non più autorizzata va scartata, altrimenti il filtro
        // chiederebbe al server dati che il server rifiuta.
        setSelected((current) => {
          const kept = current.filter((c) => allowed.has(c))
          return kept.length === current.length ? current : kept
        })
      })
      .catch((e) => setError(e instanceof ApiError ? e : new ApiError('error', 'Errore', String(e), 0)))
      .finally(() => setLoading(false))
  }, [])

  useEffect(load, [load])

  useEffect(() => {
    // Si salva solo dopo aver applicato la preferenza iniziale (altrimenti il primo render
    // la sovrascriverebbe con la selezione vuota) e solo quando c'è qualcosa da scegliere.
    if (!prefsApplied.current || stores.length <= 1) return

    window.clearTimeout(saveTimer.current)
    saveTimer.current = window.setTimeout(() => {
      void api.put('/me/preferences', { selectedStores: selected }).catch(() => {
        /* preferenza di vista: se il salvataggio fallisce, la prossima modifica riprova */
      })
    }, 800)

    return () => window.clearTimeout(saveTimer.current)
  }, [selected, stores.length])

  const value = useMemo<StoreContextValue>(
    () => ({
      stores,
      selected,
      // Filtrare su tutti equivale a non filtrare, e costa una URL che il server rifiuta.
      // Con l'elenco ancora ignoto non si collassa: «vuoto» vorrebbe dire «tutti», e una
      // schermata che partisse comunque mostrerebbe più punti vendita di quelli scelti senza
      // dirlo. Meglio la URL rifiutata, che si vede, del dato allargato, che non si vede.
      storeFilter: stores.length > 0 && selected.length >= stores.length ? [] : selected,
      storesReady: stores.length > 0,
      loading,
      error,
      select: setSelected,
      toggle: (code) =>
        setSelected((current) =>
          current.includes(code) ? current.filter((c) => c !== code) : [...current, code],
        ),
      reload: load,
      storeName: (code) => stores.find((s) => s.code === code)?.name ?? code,
    }),
    [stores, selected, loading, error, load],
  )

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>
}

export function useStores() {
  const context = useContext(StoreContext)
  if (!context) throw new Error('useStores richiede StoreProvider.')
  return context
}
